/**
 * What one company sells: add families or references to its perimeter, or
 * take them out.
 *
 * The two ways an operator describes a perimeter — "CPIT sells the tyre
 * families" and then the exceptions, pasted as a column of references. The
 * report after each change says what happened in numbers: how many articles
 * the selection named, how many were actually added or removed, and which
 * pasted references do not exist. "0 changed" is the answer that tells the
 * operator the click was a no-op rather than a failure.
 */

import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAction, useResource } from "@hardauto/core-data";
import { Field } from "@hardauto/core-forms";
import { Alert, Button, Checkbox, Dialog, Select, Textarea } from "@hardauto/core-ui";

import type { Company, PerimeterOutcome, ReferentialApi } from "../../api";
import { NAMESPACE } from "../../i18n";

export interface PerimeterDialogProps {
  api: ReferentialApi;
  company: Company | null;
  onClose: () => void;
  onChanged: () => void;
}

/** A pasted column: one reference per line, or separated by commas or semicolons. */
function parseReferences(text: string): string[] {
  return text
    .split(/[\s,;]+/u)
    .map((value) => value.trim())
    .filter(Boolean);
}

export function PerimeterDialog({ api, company, onClose, onChanged }: PerimeterDialogProps) {
  const { t } = useTranslation(NAMESPACE);
  const action = useAction();
  const [families, setFamilies] = useState<string[]>([]);
  const [references, setReferences] = useState("");
  const [mode, setMode] = useState<"add" | "remove">("add");
  const [outcome, setOutcome] = useState<PerimeterOutcome | null>(null);
  const [failed, setFailed] = useState(false);

  const familiesFetcher = useCallback(() => api.listFamilies(false), [api]);
  const list = useResource(familiesFetcher);

  const pasted = parseReferences(references);
  const empty = families.length === 0 && pasted.length === 0;

  function close() {
    setFamilies([]);
    setReferences("");
    setOutcome(null);
    setFailed(false);
    onClose();
  }

  return (
    <Dialog
      open={company !== null}
      onClose={close}
      placement="side"
      title={t("structure.perimeter.title", { company: company?.name ?? "" })}
      description={t("structure.perimeter.description")}
      closeLabel={t("structure.cancel")}
      footer={
        <>
          <Button onClick={close}>{t("structure.perimeter.close")}</Button>
          <Button
            variant="primary"
            disabled={empty}
            busy={action.isBusy("perimeter")}
            busyLabel={t("structure.save")}
            onClick={() => {
              if (!company || empty) return;
              void action.run("perimeter", async () => {
                try {
                  setOutcome(
                    await api.changePerimeter(company.code, {
                      family_codes: families,
                      references: pasted,
                      remove: mode === "remove",
                    }),
                  );
                  setFailed(false);
                  onChanged();
                } catch {
                  setFailed(true);
                }
              });
            }}
          >
            {mode === "add" ? t("structure.perimeter.add") : t("structure.perimeter.remove")}
          </Button>
        </>
      }
    >
      <div className="hardauto-structure__panel">
        {outcome ? (
          <Alert tone={outcome.unknown_references.length > 0 ? "warning" : "success"}>
            {t("structure.perimeter.outcome", {
              matched: outcome.matched,
              changed: outcome.changed,
              total: outcome.total,
            })}
            {outcome.unknown_references.length > 0
              ? ` ${t("structure.perimeter.unknown", {
                  references: outcome.unknown_references.join(", "),
                })}`
              : ""}
          </Alert>
        ) : null}
        {failed ? <Alert tone="danger">{t("error.title")}</Alert> : null}

        <Field label={t("structure.perimeter.mode")}>
          {(props) => (
            <Select
              {...props}
              value={mode}
              onChange={(event) => setMode(event.target.value as "add" | "remove")}
            >
              <option value="add">{t("structure.perimeter.modeAdd")}</option>
              <option value="remove">{t("structure.perimeter.modeRemove")}</option>
            </Select>
          )}
        </Field>

        <fieldset className="hardauto-structure__families">
          <legend>{t("structure.perimeter.families")}</legend>
          {/* A merged family is a redirection, not a family anybody sells. */}
          {(list.data?.items ?? [])
            .filter((family) => !family.merged_into)
            .map((family) => (
              <Checkbox
                key={family.code}
                label={`${family.label} (${family.code})`}
                checked={families.includes(family.code)}
                onChange={(event) => {
                  setFamilies((current) =>
                    event.target.checked
                      ? [...current, family.code]
                      : current.filter((code) => code !== family.code),
                  );
                }}
              />
            ))}
        </fieldset>

        <Field
          label={t("structure.perimeter.references")}
          hint={t("structure.perimeter.referencesHint")}
        >
          {(props) => (
            <Textarea
              {...props}
              rows={5}
              value={references}
              onChange={(event) => setReferences(event.target.value)}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}
